import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { isReviewerRole } from '../utils/releaseRoles';

/**
 * Reviewer Restrictions Interface
 * Defines what a reviewer-only team member can and cannot do
 */
export interface ReviewerRestrictions {
  isReviewer: boolean;
  isAgencyTeam: boolean;
  isGodMode: boolean;
  carteBlanche: boolean;
  canCreateCalendar: boolean;
  canEditCalendar: boolean;
  canDeleteCalendar: boolean;
  canConnectChannel: boolean;
  /** Disconnect / delete / rename channels. Creators and staff only (backend: release.reviewer_no_channel_manage). Connect + reconnect stay open. */
  canManageChannels: boolean;
  /** Brand settings (team, calendars, AI profiles, variation/industry settings); NOT account, channels, notifications */
  canAccessSettings: boolean;
  /** Create/edit/delete brand settings writers (AI brand profiles, variation/industry settings, calendars, holidays, mapping rules). Mirrors the backend rule: creators only. */
  canManageBrandSettings: boolean;
  canSchedulePost: boolean;
  canApprovePost: boolean;
  canCreateDraft: boolean;
  /** Create / duplicate / import / auto-translate variations. Creators only (backend: release.not_creator). */
  canCreateVariation: boolean;
  getRestrictionMessage: (feature: string) => string;
}

/**
 * useReviewerRestrictions Hook
 *
 * Determines if the current user has REVIEWER role in the current team
 * and returns permission flags based on that role.
 *
 * OPTIMIZED: All data now comes from the /me endpoint (auth context).
 * No separate API calls needed for membership or approval workflow.
 *
 * REVIEWER-only people (a reviewer role, not agency staff in God Mode) have restricted access:
 * - Can view dashboard, calendar, posts
 * - Can create DRAFT posts
 * - Can release posts (if the release workflow is on)
 * - CAN connect and reconnect channels
 * - CANNOT disconnect, delete or rename channels (decision 2026-10-15; backend 403 release.reviewer_no_channel_manage)
 * - CANNOT create/edit/delete calendars or change brand settings
 *   (team, calendars, AI profiles, variation/industry settings)
 * - CANNOT schedule posts directly (unless carte blanche)
 *
 * @returns ReviewerRestrictions object with permission flags
 *
 * @example
 * const { isReviewer, canCreateCalendar, getRestrictionMessage } = useReviewerRestrictions();
 *
 * if (!canCreateCalendar) {
 *   console.log(getRestrictionMessage('create calendar'));
 * }
 */
export const useReviewerRestrictions = (): ReviewerRestrictions => {
  const { user } = useAuth();

  // Detect God Mode: agency staff accessing a brand workspace
  // God Mode scenarios:
  // 1. currentTeamId is set but teamId is null (agency staff with no home team)
  // 2. currentTeamId differs from teamId (switched to a different workspace)
  const isGodMode = !!(
    user?.currentTeamId &&
    (user?.teamId === null || user?.teamId === undefined || user.currentTeamId !== user.teamId)
  );

  // Get effective team data (currentTeam in God Mode, otherwise home team)
  const effectiveTeam = isGodMode ? user?.currentTeam : user?.team;

  const restrictions = useMemo((): ReviewerRestrictions => {
    // Use teamRole from auth context (REVIEWER role restricts access)
    const isReviewer = isReviewerRole(user?.teamRole);
    // Agency staff inside a brand (God Mode) keep their staff rights whatever their home role says
    const isReviewerOnly = isReviewer && !isGodMode;

    // Get isAgencyTeam and carteBlanche from the effective team
    // These are now included in the /me response
    const isAgencyTeam = effectiveTeam?.isAgencyTeam ?? false;
    const carteBlanche = effectiveTeam?.carteBlanche ?? true; // Default to true if no workflow

    return {
      isReviewer,
      isAgencyTeam,
      isGodMode,
      carteBlanche,

      // Calendar permissions
      canCreateCalendar: !isReviewerOnly,
      canEditCalendar: !isReviewerOnly,
      canDeleteCalendar: !isReviewerOnly,

      // Channel permissions: reviewers connect and reconnect, but do not disconnect / delete / rename
      canConnectChannel: true,
      canManageChannels: !isReviewerOnly,

      // Brand settings (not account, channels, notifications, affiliate)
      canAccessSettings: !isReviewerOnly,
      canManageBrandSettings: !isReviewerOnly,

      // Post permissions
      // Can schedule if:
      // 1. Carte blanche is ON (no approval needed), OR
      // 2. Not a reviewer-only member AND not in god mode (normal user on their own team)
      // This prevents both reviewer-only members and staff in god mode from bypassing release
      canSchedulePost: carteBlanche || (!isReviewer && !isGodMode),
      canApprovePost: true, // Reviewers and team members can release
      canCreateDraft: true, // Reviewers CAN create draft posts
      // Mirrors backend assertCanCreateVariation: blocked only for reviewer-only people AND an active release workflow
      // (agency brand, carte blanche off). Brand settings / channel disconnect have NO workflow condition (isCreator only).
      canCreateVariation: !(isReviewerOnly && isAgencyTeam && !carteBlanche),

      // Get helpful message for restricted features
      getRestrictionMessage: (feature: string) => {
        // Handle god mode restrictions
        if (isGodMode && !carteBlanche) {
          switch (feature.toLowerCase()) {
            case 'schedule post':
            case 'shoot post':
              return 'This brand requires release first. Content must be released before it can be scheduled or published.';
            default:
              return '';
          }
        }

        if (!isReviewer) return '';

        const defaultMessage = `Reviewers cannot ${feature}.`;

        switch (feature.toLowerCase()) {
          case 'create calendar':
          case 'edit calendar':
          case 'delete calendar':
            return 'Calendar management is not available for reviewers.';

          case 'access settings':
            return 'Brand settings are not available for reviewers.';

          case 'schedule post':
          case 'shoot post':
            if (carteBlanche) {
              return ''; // No restriction
            }
            return 'Content must be released before it can be scheduled. Send it for review first.';

          default:
            return defaultMessage;
        }
      },
    };
  }, [user?.teamRole, effectiveTeam?.isAgencyTeam, effectiveTeam?.carteBlanche, isGodMode]);

  return restrictions;
};
